import { motion, AnimatePresence } from "framer-motion";
import { Brain, ArrowLeft, CheckCircle2, Mail, LogIn, RefreshCw, Check, Microscope, AlertTriangle, Calculator, ClipboardList, Map, Network, Database, BookOpen, Activity, Globe, Lock, ExternalLink, Award, Zap, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ROUTE_PATHS, ONCO_TREINO_MODULES, CHECKOUT_URL } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const iconMap = {
  Microscope,
  AlertTriangle,
  Calculator,
  ClipboardList,
  Map,
  Network,
  Database
};

function CasoClinicoDemo() {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const handleAnswer = (index: number) => {
    setSelectedAnswer(index);
    setShowFeedback(true);
  };

  const handleReset = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);
  };

  const alternatives = [
    "Administrar anti-histamínico e observar",
    "Iniciar antibioticoterapia empírica imediatamente",
    "Solicitar urinocultura e aguardar resultado",
    "Realizar ECG e avaliar toxicidade cardíaca"
  ];

  return (
    <div className="card-premium p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-destructive" />
          <div className="w-3 h-3 rounded-full bg-gold" />
          <div className="w-3 h-3 rounded-full bg-primary" />
        </div>
        <div className="flex-1 bg-muted/30 rounded-lg px-4 py-2 text-sm text-muted-foreground font-mono">
          onco-treino.kiwify.app · Simulador Clínico
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-mono mb-3">
            Caso #047
          </div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">Câncer de Mama · Nível Intermediário</span>
            <div className="flex gap-0.5">
              {[1, 2, 3].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-gold text-gold" />
              ))}
              {[4, 5].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 text-muted-foreground/30" />
              ))}
            </div>
          </div>
          <h3 className="text-xl font-heading font-bold mb-3">
            Neutropenia Febril Pós-Quimioterapia
          </h3>
          <p className="text-muted-foreground mb-6">
            Paciente de 52 anos, em 3º ciclo de AC para câncer de mama, apresenta febre de 38.6°C e neutrófilos de 480/mm³. Qual a principal hipótese diagnóstica e conduta de enfermagem imediata?
          </p>
        </div>

        <div className="space-y-3">
          {alternatives.map((alt, index) => (
            <motion.button
              key={index}
              onClick={() => handleAnswer(index)}
              disabled={showFeedback}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                selectedAnswer === index
                  ? index === 1
                    ? "border-primary bg-primary/10"
                    : "border-destructive/50 bg-destructive/5"
                  : "border-border hover:border-primary/50 hover:bg-muted/30"
              } ${showFeedback ? "cursor-default" : "cursor-pointer"}`}
              whileHover={!showFeedback ? { scale: 1.01 } : {}}
              whileTap={!showFeedback ? { scale: 0.99 } : {}}
            >
              <div className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  selectedAnswer === index
                    ? index === 1
                      ? "border-primary bg-primary"
                      : "border-destructive bg-destructive"
                    : "border-muted-foreground/30"
                }`}>
                  {selectedAnswer === index && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className="text-sm">{alt}</span>
              </div>
            </motion.button>
          ))}
        </div>

        <AnimatePresence>
          {showFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className={`p-6 rounded-xl border ${
                selectedAnswer === 1
                  ? "border-primary/30 bg-primary/5"
                  : "border-destructive/30 bg-destructive/5"
              }`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  selectedAnswer === 1
                    ? "bg-primary text-white"
                    : "bg-destructive text-white"
                }`}>
                  {selectedAnswer === 1 ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-lg font-bold">✕</span>
                  )}
                </div>
                <div>
                  <h4 className="font-heading font-bold text-lg mb-2">
                    {selectedAnswer === 1 ? "Resposta Correta!" : "Resposta Incorreta"}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    {selectedAnswer === 1
                      ? "A neutropenia febril é uma emergência oncológica. A antibioticoterapia empírica de amplo espectro deve ser iniciada imediatamente, sem aguardar resultados de culturas, para reduzir morbimortalidade. O enfermeiro deve monitorar sinais vitais, coletar hemoculturas antes da primeira dose e comunicar imediatamente a equipe médica."
                      : "A conduta correta é iniciar antibioticoterapia empírica imediatamente. Aguardar resultados de exames ou administrar apenas sintomáticos pode comprometer o prognóstico do paciente com neutropenia febril."}
                  </p>
                  <div className="text-xs text-muted-foreground">
                    <strong>Referência:</strong> INCA - Protocolo de Neutropenia Febril (2026)
                  </div>
                </div>
              </div>

              <button
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar outro caso
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground text-center">
            Caso 47 de 102 casos disponíveis
          </div>
        </div>
      </div>
    </div>
  );
}

function CalculadoraSC() {
  const [peso, setPeso] = useState(70);
  const [altura, setAltura] = useState(170);

  const sc = Math.sqrt((peso * altura) / 3600);

  const getInterpretacao = () => {
    if (sc < 1.5) return { text: "Superfície corporal reduzida - ajuste de dose pode ser necessário", color: "text-gold" };
    if (sc >= 1.5 && sc <= 2.0) return { text: "Superfície corporal dentro da faixa normal", color: "text-primary" };
    return { text: "Superfície corporal elevada - atenção ao cálculo de dose", color: "text-gold" };
  };

  const interpretacao = getInterpretacao();

  return (
    <div className="card-premium p-8">
      <h3 className="text-2xl font-heading font-bold mb-6">Calculadora de Superfície Corporal</h3>

      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium">Peso (kg)</label>
            <span className="text-2xl font-heading font-bold text-primary">{peso} kg</span>
          </div>
          <input
            type="range"
            min="30"
            max="150"
            value={peso}
            onChange={(e) => setPeso(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium">Altura (cm)</label>
            <span className="text-2xl font-heading font-bold text-primary">{altura} cm</span>
          </div>
          <input
            type="range"
            min="140"
            max="210"
            value={altura}
            onChange={(e) => setAltura(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div className="pt-6 border-t border-border">
          <div className="text-center mb-4">
            <div className="text-sm text-muted-foreground mb-2">Superfície Corporal (Fórmula de Mosteller)</div>
            <div className="text-5xl font-heading font-bold text-glow-violet mb-2">
              {sc.toFixed(2)} m²
            </div>
          </div>

          <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
            <p className={`text-sm text-center ${interpretacao.color}`}>
              {interpretacao.text}
            </p>
          </div>
        </div>

        <div className="text-xs text-muted-foreground text-center">
          <strong>Fórmula:</strong> SC (m²) = √[(Peso × Altura) / 3600]
        </div>
      </div>
    </div>
  );
}

export default function OncoTreino() {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 left-0 right-0 z-50 h-20 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-heading font-bold text-glow-violet block leading-none">OncoTreino IA</span>
              <span className="text-xs text-muted-foreground">Bônus Exclusivo Incluso no Pacote</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to={ROUTE_PATHS.HOME}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Voltar ao curso</span>
            </Link>
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta-gold px-6 py-3 text-base"
            >
              Garantir Acesso
            </a>
          </div>
        </div>
      </header>

      <main className="pt-20">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(139, 92, 246, 0.15) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-6xl mx-auto"
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div variants={staggerItem}>
                  <span className="section-tag mb-6">
                    <span className="text-gold">★</span> Bônus Exclusivo
                  </span>

                  <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                    OncoTreino IA<br />
                    <span className="text-glow-violet">Plataforma Interativa</span><br />
                    de Treinamento Oncológico
                  </h1>

                  <p className="text-xl text-muted-foreground mb-8">
                    Acesse direto pela <strong className="text-primary">plataforma Kiwify</strong> após a compra do Mini Curso. Sem login adicional, sem complicações.
                  </p>

                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="card-premium p-4 text-center">
                      <Microscope className="w-6 h-6 text-primary mx-auto mb-2" />
                      <div className="text-3xl font-heading font-bold text-glow-violet mb-1">102</div>
                      <div className="text-xs text-muted-foreground">Casos Clínicos</div>
                    </div>
                    <div className="card-premium p-4 text-center">
                      <BookOpen className="w-6 h-6 text-primary mx-auto mb-2" />
                      <div className="text-3xl font-heading font-bold text-glow-violet mb-1">90</div>
                      <div className="text-xs text-muted-foreground">Questões com Gabarito</div>
                    </div>
                    <div className="card-premium p-4 text-center">
                      <Activity className="w-6 h-6 text-primary mx-auto mb-2" />
                      <div className="text-3xl font-heading font-bold text-glow-violet mb-1">7</div>
                      <div className="text-xs text-muted-foreground">Módulos Interativos</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                      <Globe className="w-3.5 h-3.5" />
                      Acesso via Kiwify
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                      <Lock className="w-3.5 h-3.5" />
                      Login na Kiwify
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                      <ExternalLink className="w-3.5 h-3.5" />
                      Link direto na aba
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={staggerItem} className="relative">
                  <div className="relative rounded-2xl overflow-hidden">
                    <img
                      src={IMAGES.ONCO_TREINO_BONUS}
                      alt="OncoTreino IA Platform"
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 ring-2 ring-inset ring-primary/30 rounded-2xl" />
                    <div className="absolute -inset-2 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur-3xl opacity-60 -z-10" />
                    <div className="absolute top-4 right-4 px-4 py-2 rounded-full bg-background/90 backdrop-blur-sm border border-primary/30 text-sm font-medium">
                      7 Módulos / Plataforma completa
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 section-alt">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  Como <span className="text-glow-violet">Funciona</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Acesso simples e direto em 3 passos
                </p>
              </motion.div>

              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                  <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-gold -translate-y-1/2 -z-10" />

                  <motion.div variants={staggerItem} className="relative">
                    <div className="card-premium p-8 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-sm font-mono text-primary mb-2">Passo 01</div>
                      <h3 className="text-xl font-heading font-bold mb-3">Adquira o Mini Curso</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Faça sua compra e confirme o pagamento de forma segura via Kiwify.
                      </p>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                        <Lock className="w-3 h-3" />
                        Pagamento 100% seguro
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={staggerItem} className="relative">
                    <div className="card-premium p-8 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary to-accent flex items-center justify-center mx-auto mb-6" style={{ background: "linear-gradient(135deg, #a78bfa 0%, #a78bfa 100%)" }}>
                        <Mail className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-sm font-mono mb-2" style={{ color: "#a78bfa" }}>Passo 02</div>
                      <h3 className="text-xl font-heading font-bold mb-3">Receba o E-mail da Kiwify</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Você receberá um e-mail de confirmação para criar seu login e senha na plataforma Kiwify.
                      </p>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium" style={{ backgroundColor: "rgba(167, 139, 250, 0.1)", borderColor: "rgba(167, 139, 250, 0.2)", color: "#a78bfa" }}>
                        <Check className="w-3 h-3" />
                        Acesso imediato após confirmação
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={staggerItem} className="relative">
                    <div className="card-premium p-8 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold to-amber-500 flex items-center justify-center mx-auto mb-6">
                        <LogIn className="w-8 h-8 text-black" />
                      </div>
                      <div className="text-sm font-mono text-gold mb-2">Passo 03</div>
                      <h3 className="text-xl font-heading font-bold mb-3">Acesse o OncoTreino IA</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Dentro da plataforma Kiwify há uma aba com acesso direto ao OncoTreino IA.
                      </p>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold text-xs font-medium">
                        <ExternalLink className="w-3 h-3" />
                        Acesso via aba dentro da Kiwify
                      </div>
                    </div>
                  </motion.div>
                </div>

                <motion.div variants={staggerItem} className="mt-12">
                  <div className="p-6 rounded-xl bg-gold/5 border border-gold/20">
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Sem aplicativo extra — tudo dentro da Kiwify.</strong> Após a compra, você receberá um e-mail com o link para criar seu login e senha na Kiwify. Dentro da plataforma, acesse a aba do OncoTreino IA com um clique.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  Caso Clínico <span className="text-glow-violet">Interativo</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Veja como funciona o simulador de casos clínicos com feedback em tempo real
                </p>
              </motion.div>

              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <motion.div variants={staggerItem}>
                    <h3 className="text-2xl font-heading font-bold mb-4">
                      Treine Decisões Clínicas Reais
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Cada caso apresenta uma situação clínica real da oncologia. Você escolhe a conduta e recebe feedback imediato com gabarito comentado e referências científicas.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">102 casos clínicos distribuídos em 6 tipos de câncer</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Feedback imediato com gabarito comentado</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Referências científicas atualizadas</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Sistema de pontuação para acompanhar evolução</span>
                      </li>
                    </ul>
                  </motion.div>

                  <motion.div variants={staggerItem}>
                    <CasoClinicoDemo />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 section-alt">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  Os 7 Módulos da <span className="text-glow-violet">Plataforma</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Ferramentas interativas para treinar raciocínio clínico e tomada de decisão em oncologia
                </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {ONCO_TREINO_MODULES.map((module, index) => {
                  const iconNames = ['Microscope', 'AlertTriangle', 'Calculator', 'ClipboardList', 'Map', 'Network', 'Database'];
                  const IconComponent = iconMap[iconNames[index] as keyof typeof iconMap];
                  if (!IconComponent) return null;

                  return (
                    <motion.div
                      key={module.id}
                      variants={staggerItem}
                      className="card-premium p-8 relative"
                    >
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary" />

                      <div className="flex items-start gap-4 mb-6">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-primary/20 to-secondary/20">
                          <IconComponent className="w-7 h-7 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-mono text-muted-foreground mb-1">
                            Módulo {String(index + 1).padStart(2, '0')}
                          </div>
                          <h3 className="text-xl font-heading font-bold mb-2">{module.title}</h3>
                          <p className="text-sm text-muted-foreground">{module.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                          <div className="text-2xl font-heading font-bold text-primary mb-1">{module.cases}</div>
                          <div className="text-xs text-muted-foreground">Casos Clínicos</div>
                        </div>
                        <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                          <div className="text-2xl font-heading font-bold text-primary mb-1">{module.questions}</div>
                          <div className="text-xs text-muted-foreground">Questões</div>
                        </div>
                      </div>

                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full" style={{ width: '0%' }} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">Progresso: 0%</div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  Calculadora Oncológica <span className="text-glow-violet">ao Vivo</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Ferramentas de cálculo com interpretação clínica em tempo real
                </p>
              </motion.div>

              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <motion.div variants={staggerItem}>
                    <h3 className="text-2xl font-heading font-bold mb-4">
                      Cálculos Essenciais para Oncologia
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      A plataforma inclui calculadoras interativas para os principais cálculos utilizados na prática oncológica, com interpretação clínica automática.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Superfície corporal (Fórmula de Mosteller)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Clearance de creatinina (Cockcroft-Gault)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Escala ECOG de performance status</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Índice de Karnofsky com interpretação</span>
                      </li>
                    </ul>
                  </motion.div>

                  <motion.div variants={staggerItem}>
                    <CalculadoraSC />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 section-alt">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <span className="section-tag mb-6">
                  <span className="text-gold">★</span> Oferta Especial
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  Garanta Seu Acesso <span className="text-glow-violet">Agora</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Mini Curso + OncoTreino IA por apenas
                </p>
              </motion.div>

              <div className="max-w-2xl mx-auto">
                <motion.div variants={staggerItem}>
                  <div className="card-premium p-12 text-center" style={{ background: 'linear-gradient(145deg, #100d1c 0%, #0b0814 100%)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold to-amber-500" />
                    
                    <div className="flex items-center justify-center gap-2 mb-6">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className="w-6 h-6 fill-gold text-gold" />
                      ))}
                    </div>

                    <div className="mb-8">
                      <div className="text-6xl md:text-7xl font-heading font-bold text-glow-gold mb-3">12x de R$ 20,37</div>
                      <div className="text-sm text-muted-foreground mb-6">ou R$ 197,00 à vista no Pix</div>
                      
                      <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                          <Check className="w-4 h-4" />
                          Cartão em 12x
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 text-gold text-sm font-medium">
                          <Check className="w-4 h-4" />
                          Pix à vista
                        </div>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8 text-left max-w-md mx-auto">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">6 aulas completas do Mini Curso</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">OncoTreino IA com 7 módulos interativos</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">102 casos clínicos + 90 questões comentadas</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Acesso via Kiwify (sem login adicional)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Certificado de conclusão</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Garantia incondicional de 7 dias</span>
                      </li>
                    </ul>

                    <a
                      href={CHECKOUT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-cta-gold mb-6"
                    >
                      Quero Garantir Meu Acesso — 12x de R$ 20,37
                    </a>

                    <div className="mb-6">
                      <img
                        src={IMAGES.GARANTIA_BADGE}
                        alt="Garantia de 7 dias"
                        className="w-32 h-auto mx-auto"
                      />
                    </div>

                    <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-primary" />
                        <span>Pagamento Seguro</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Award className="w-3 h-3 text-primary" />
                        <span>Certificado Incluso</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-primary" />
                        <span>Acesso Imediato</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <footer className="py-12 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-glow-violet">OncoTreino IA</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Plataforma interativa de treinamento em enfermagem oncológica.
            </p>
            <div className="line-glow max-w-xs mx-auto my-6" />
            <p className="text-xs text-muted-foreground">
              © 2026 OncoTreino IA. Todos os direitos reservados.
            </p>
            <p className="text-xs text-muted-foreground italic">
              Ferramenta educacional — não substitui conduta médica ou avaliação profissional.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}